import {
  Building2,
  FileText,
  Info,
  MapPin,
  Mic,
  Sparkles,
  Tag,
  Users,
} from "lucide-react";

import type {
  InformationItem,
  Partner,
  Signal,
  Speaker,
  Track,
  Venue,
} from "../../event-experience/schemas";

import type { AttendeeRecord } from "../use-attendee-records";

import type { EntitySpec } from "./EntityCrudSection";

/**
 * What `safeHttpUrl` will later accept. Every URL here is rendered through it
 * on the attendee side, so a value without a scheme is stored, echoed back in
 * the admin list, and then produces no link at all on the page it was typed
 * for. Checked at the form because the rules only bound the length.
 */
const HTTP_URL = /^https?:\/\/[^\s]+$/i;
const HTTP_URL_MESSAGE = "Enter a full address starting with https://.";
const LINKEDIN_URL =
  /^https:\/\/(?:[a-z0-9-]+\.)*linkedin\.com(?:[/?#][^\s]*)?$/i;
const LINKEDIN_URL_MESSAGE = "Enter an https://linkedin.com address.";

/**
 * The write contracts for the content collections, as data.
 *
 * Every bound here is copied from `firestore.rules`, so the form refuses what
 * the rules would refuse and says which field is wrong. The subtler entries:
 *
 *  - `createOnly` marks fields the rules exclude from the update key list;
 *    sending one on an update is refused even if the value is unchanged.
 *  - tracks, speakers and venues delete through a command, because their
 *    triggers sweep references and write a tombstone. Partners and
 *    information items are client-deletable and vanish immediately.
 *  - information items are the only collection whose update must carry a
 *    timestamp (`updatedAt == request.time`).
 */

export const trackSpec: EntitySpec<Track> = {
  collection: "tracks",
  singular: "track",
  plural: "tracks",
  icon: Tag,
  testId: "track",
  deleteCommand: { type: "DELETE_TRACK", payloadKey: "trackId" },
  fields: [
    {
      name: "name",
      label: "Name",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    {
      name: "color",
      label: "Colour",
      kind: "color",
      required: true,
      defaultValue: "#1d4ed8",
      help: "Choose the colour used for this track.",
    },
    {
      name: "description",
      label: "Description",
      kind: "textarea",
      maxLength: 5000,
    },
    {
      name: "displayOrder",
      label: "Display order",
      kind: "order",
      min: 0,
    },
    // Shown in the attendee's "Your track" banner and filled in for {room},
    // {question} and {champion} in a personalised announcement. clearWhenBlank
    // so clearing one removes it, and the announcement says "TBA" rather than
    // an empty gap.
    {
      name: "room",
      label: "Room",
      kind: "text",
      maxLength: 200,
      clearWhenBlank: true,
      placeholder: "e.g. Hall B",
    },
    {
      name: "question",
      label: "Theme question",
      kind: "textarea",
      maxLength: 500,
      clearWhenBlank: true,
      help: "The question this track works on. Attendees see it under their track.",
    },
    // A link, never a copied name: the rules hold it to one attendee OR one
    // speaker, and it is shown as whatever that person's name is now.
    // TracksSection supplies the choices.
    {
      name: "champion",
      label: "Champion",
      kind: "lookup",
      storedAs: ["championAttendeeId", "championSpeakerId"],
      help: "Search the attendees and speakers. Attendees see the champions under their track and can open their profile.",
    },
    // The same kind of link. Either may be empty; the picker will not offer
    // the same person for both.
    {
      name: "secondChampion",
      label: "Second champion",
      kind: "lookup",
      storedAs: ["secondChampionAttendeeId", "secondChampionSpeakerId"],
      help: "Optional. A track can name two champions.",
    },
  ],
  primary: (track) => track.name,
  secondary: (track) => track.description,
  toForm: (track) => ({
    name: track.name,
    color: track.color ?? "",
    description: track.description ?? "",
    displayOrder: track.displayOrder ?? 0,
    room: track.room ?? "",
    question: track.question ?? "",
    champion: championValue(track),
    secondChampion: secondChampionValue(track),
  }),
};

/**
 * The champion lookup's value for a track: `<key>:<documentId>`, naming which
 * of the two link fields holds it (see `storedAs`), or "" when unset.
 */
export function championValue(
  track: Pick<Track, "championAttendeeId" | "championSpeakerId">,
): string {
  if (track.championAttendeeId)
    return `championAttendeeId:${track.championAttendeeId}`;
  if (track.championSpeakerId)
    return `championSpeakerId:${track.championSpeakerId}`;
  return "";
}

/** The second champion lookup's value, in the same shape. */
export function secondChampionValue(
  track: Pick<Track, "secondChampionAttendeeId" | "secondChampionSpeakerId">,
): string {
  if (track.secondChampionAttendeeId)
    return `secondChampionAttendeeId:${track.secondChampionAttendeeId}`;
  if (track.secondChampionSpeakerId)
    return `secondChampionSpeakerId:${track.secondChampionSpeakerId}`;
  return "";
}

export const speakerSpec: EntitySpec<Speaker> = {
  collection: "speakers",
  singular: "speaker",
  plural: "speakers",
  icon: Mic,
  testId: "speaker",
  deleteCommand: { type: "DELETE_SPEAKER", payloadKey: "speakerId" },
  fields: [
    {
      name: "name",
      label: "Name",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    // role and company look optional but isValidSpeaker requires both.
    {
      name: "role",
      label: "Role",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    {
      name: "company",
      label: "Company",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    { name: "bio", label: "Bio", kind: "textarea", maxLength: 10000 },
    // An upload to events/{eventId}/speakers/{speakerId}/, 5 MB in
    // storage.rules. clearWhenBlank so Remove takes the key off rather than
    // storing "". A URL stored before uploads existed, or copied from the
    // roster by the link below, stays until it is replaced.
    {
      name: "avatar",
      label: "Photo",
      kind: "image",
      maxLength: 2048,
      clearWhenBlank: true,
      image: { maxBytes: 5 * 1024 * 1024, shape: "portrait" },
      help: "A square photo of at least 400 by 400 pixels works best; it is cropped to a circle. PNG, JPEG or WebP, up to 5 MB.",
    },
    {
      name: "displayOrder",
      label: "Display order",
      kind: "order",
      min: 0,
    },
    // The link to the roster, shown first by `linkFirst` below.
    // `hasLiveSpeakerAttendee` refuses an id that names no live attendee, so
    // the picker offers the roster rather than a text box; clearWhenBlank
    // because unlinking has to remove the key, and an empty string would fail
    // that rule rather than clear it.
    {
      name: "attendeeId",
      label: "Attendee on the roster",
      kind: "lookup",
      maxLength: 200,
      clearWhenBlank: true,
      fills: ["name", "role", "company", "bio", "avatar"],
      help: "Search the attendee list first. Picking someone copies their name, job title, company, bio and photo into this speaker, puts their LinkedIn link and signals on the speaker profile, and keeps them from being offered twice on a session's presenter list.",
    },
  ],
  linkFirst: {
    field: "attendeeId",
    manualLabel: "Not on the roster? Enter their details",
  },
  thumbnail: (speaker) => speaker.avatar,
  primary: (speaker) => speaker.name,
  secondary: (speaker) =>
    [
      speaker.role,
      speaker.company,
      speaker.attendeeId ? "on the roster" : undefined,
    ]
      .filter(Boolean)
      .join(" · "),
  toForm: (speaker) => ({
    name: speaker.name,
    role: speaker.role ?? "",
    company: speaker.company ?? "",
    bio: speaker.bio ?? "",
    avatar: speaker.avatar ?? "",
    displayOrder: speaker.displayOrder ?? 0,
    attendeeId: speaker.attendeeId ?? "",
  }),
};

export const partnerSpec: EntitySpec<Partner> = {
  collection: "partners",
  singular: "partner",
  plural: "partners",
  icon: Building2,
  testId: "partner",
  fields: [
    {
      name: "name",
      label: "Name",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    // logo is required and must be non-empty, unlike most optional URLs here.
    // An upload to events/{eventId}/partners/{partnerId}/, 5 MB in
    // storage.rules. A URL pasted before uploads existed keeps working until
    // it is replaced.
    {
      name: "logo",
      label: "Logo",
      kind: "image",
      required: true,
      maxLength: 2048,
      image: { maxBytes: 5 * 1024 * 1024, shape: "logo" },
      help: "A logo on a transparent or white background, at least 400 pixels wide. It is shown whole, never cropped. PNG, JPEG or WebP, up to 5 MB.",
    },
    // 5000 is the track and venue description bound and the rules bound in
    // isValidPartner. Deliberately not tightened: a partner already storing
    // more would then fail the form on an edit that never touched the
    // description. What was actually unbounded was the tile, which printed the
    // whole thing; it clamps now, and the full text is behind opening it.
    {
      name: "description",
      label: "Description",
      kind: "textarea",
      maxLength: 5000,
    },
    {
      name: "websiteUrl",
      label: "Website",
      kind: "text",
      maxLength: 2048,
      pattern: HTTP_URL,
      patternMessage: HTTP_URL_MESSAGE,
    },
  ],
  primary: (partner) => partner.name,
  secondary: (partner) => partner.websiteUrl,
  thumbnail: (partner) => partner.logo,
  toForm: (partner) => ({
    name: partner.name,
    logo: partner.logo ?? "",
    description: partner.description ?? "",
    websiteUrl: partner.websiteUrl ?? "",
  }),
};

export const venueSpec: EntitySpec<Venue> = {
  collection: "venues",
  singular: "venue",
  plural: "venues",
  icon: MapPin,
  testId: "venue",
  deleteCommand: { type: "DELETE_VENUE", payloadKey: "venueId" },
  fields: [
    {
      name: "name",
      label: "Name",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    {
      name: "address",
      label: "Address",
      kind: "text",
      required: true,
      maxLength: 1000,
    },
    {
      name: "description",
      label: "Description",
      kind: "textarea",
      maxLength: 5000,
      help: "Shown on the venue card: what the building is, how to find the entrance, anything a map pin does not say.",
    },
  ],
  primary: (venue) => venue.name,
  secondary: (venue) => venue.address,
  toForm: (venue) => ({
    name: venue.name,
    address: venue.address ?? "",
    description: venue.description ?? "",
  }),
};

/**
 * Signals: the pre-loaded list attendees pick from on first login (for
 * Innovation Days, transcribed from the Google-form results). Client-deletable
 * like partners — a deleted signal's id may linger inside existing selections,
 * and every reader resolves ids against this list and drops unknown ones.
 */
export const signalSpec: EntitySpec<Signal> = {
  collection: "signals",
  singular: "signal",
  plural: "signals",
  icon: Sparkles,
  testId: "signal",
  fields: [
    {
      name: "label",
      label: "Label",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    {
      name: "displayOrder",
      label: "Display order",
      kind: "order",
      min: 0,
    },
  ],
  primary: (signal) => signal.label,
  toForm: (signal) => ({
    label: signal.label,
    displayOrder: signal.displayOrder ?? 0,
  }),
};

export const informationSpec: EntitySpec<InformationItem> = {
  collection: "information_items",
  singular: "item",
  plural: "items",
  icon: Info,
  testId: "information",
  createTimestamps: ["createdAt", "updatedAt"],
  updateTimestamps: ["updatedAt"],
  fields: [
    // Written, never shown. `isValidInformationItem` requires the key and pins
    // it to the enum, and Q & A is the only kind the section offers now. An
    // item stored as INFORMATION keeps that value through an edit rather than
    // being rewritten under the organiser; the rules still accept it, so those
    // rows stay editable and deletable until somebody clears them.
    {
      name: "kind",
      label: "Kind",
      kind: "select",
      hidden: true,
      defaultValue: "Q_AND_A",
      options: [
        { value: "INFORMATION", label: "Information" },
        { value: "Q_AND_A", label: "Q & A" },
      ],
    },
    {
      name: "title",
      label: "Title",
      kind: "text",
      required: true,
      maxLength: 500,
    },
    {
      name: "body",
      label: "Body",
      kind: "textarea",
      required: true,
      maxLength: 20000,
    },
    {
      name: "displayOrder",
      label: "Display order",
      kind: "order",
      min: 0,
      max: 10000,
    },
    { name: "isPublished", label: "Published", kind: "switch" },
  ],
  primary: (item) => item.title,
  secondary: (item) => (item.isPublished ? undefined : "draft"),
  toForm: (item) => ({
    kind: item.kind,
    title: item.title,
    body: item.body ?? "",
    displayOrder: item.displayOrder ?? 0,
    isPublished: item.isPublished ?? false,
  }),
};

export const resourceIcon = FileText;

/**
 * Attendees. Unlike the content collections above, these carry PII — email
 * addresses — which is why only event admins may list them and why the
 * participant-facing directory is a separate, projected collection.
 *
 * `trackId` is deliberately not a field here. It appears in no update key
 * list because assigning a track has to touch the attendee and their linked
 * membership together, so it is a command; AttendeesSection supplies that as a
 * `rowAction`.
 *
 * `status` offers only pending and confirmed. `isValidAttendee` also accepts
 * `withdrawn`, but create refuses it, and withdrawal is the registration
 * command's business rather than something an admin types over.
 *
 * Its `help` says what the choice does, because the default is pending and the
 * two words are the organiser's only clue. Both are in the participant
 * directory (`attendee_projection.py`), registration, the importer and this
 * form (for an address that already has an account) set confirmed by
 * themselves, and the reminder mail goes to confirmed rows alone
 * (`firestore_event_reminder_repository.py:47`).
 */
export const attendeeSpec: EntitySpec<AttendeeRecord> = {
  collection: "attendees",
  singular: "attendee",
  plural: "attendees",
  icon: Users,
  testId: "attendee",
  deleteClearsReferences: true,
  bulkDelete: true,
  // Creation is a command. Rules cannot compute sha256, so they cannot check
  // that a document id matches the email inside it; the trigger derives the
  // id — sha256 of the email when there is one, a random id when there is
  // not, which is what makes an email-less record a listing nothing can
  // ever claim — and `allow create: if false` closes the direct write
  // behind it.
  createCommand: { type: "ADD_EVENT_ATTENDEE" },
  fields: [
    {
      name: "name",
      label: "Name",
      kind: "text",
      required: true,
      maxLength: 200,
    },
    {
      name: "email",
      label: "Email",
      kind: "text",
      maxLength: 320,
      // Create-only, and omitted from the payload when blank: with an
      // address the record sits at sha256 of it, which is what a
      // registration or a later import links to. Without one it is a
      // directory-only listing at an id nothing can derive, so it can never
      // be claimed or merged. A present-but-empty email is refused by the
      // rules, so the key must be absent, not empty. `email` is in no update
      // key list, so the edit dialog omits the field as well as the write.
      createOnly: true,
      pattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
      patternMessage: "Enter a valid email address.",
      help: "Recommended: it links this record to the person when they register. Leave it blank for someone who will not register themselves; they appear on the attendee list, but nothing can link them. Correcting an address later means deleting this record and adding it again, which ends their access unless they administer the event, drops them from any session they present, and unlinks any speaker profile.",
    },
    // alwaysSend: isValidAttendee lists both in hasAll, so the key must be
    // present on create even when the admin leaves it blank. Empty passes
    // isOptionalBoundedString; absent fails hasAll.
    {
      name: "role",
      label: "Job title",
      kind: "text",
      maxLength: 200,
      alwaysSend: true,
    },
    // The business units, plus Other for anyone outside them. Same control and
    // same vocabulary as a person's own profile, and the same list the
    // spreadsheet import resolves an internal attendee's company against.
    // A stored company that is not a unit survives an edit untouched.
    {
      name: "company",
      label: "Company",
      kind: "company",
      maxLength: 200,
      alwaysSend: true,
    },
    {
      name: "attendeeType",
      label: "Type",
      kind: "select",
      required: true,
      defaultValue: "internal",
      options: [
        { value: "internal", label: "Internal" },
        { value: "external", label: "External" },
      ],
    },
    {
      name: "status",
      label: "Status",
      kind: "select",
      required: true,
      defaultValue: "pending",
      options: [
        { value: "pending", label: "Pending" },
        { value: "confirmed", label: "Confirmed" },
      ],
      help: "Both statuses put the person in the attendee directory. Confirmed means the address already has a Connect account. When it does, saving sets confirmed and gives them the event straight away, as importing and signing in do. The event reminder email goes to confirmed attendees only.",
    },
    { name: "bio", label: "Bio", kind: "textarea", maxLength: 10000 },
    {
      name: "linkedinUrl",
      label: "LinkedIn",
      kind: "text",
      maxLength: 2048,
      pattern: LINKEDIN_URL,
      patternMessage: LINKEDIN_URL_MESSAGE,
    },
    // An upload to events/{eventId}/attendees/{attendeeId}/, 5 MB in
    // storage.rules, the same field speakers and partners use. The id is in
    // the path and creation is a command whose trigger derives the id, so a
    // photo is added once the attendee exists (see EntityCrudSection). A URL
    // stored before uploads existed, including a profile photo the person's
    // own profile copied in, shows and survives an edit that does not touch
    // it. The replaced upload is deleted by the backend once nothing shows it
    // (entity_image_cleanup.py). clearWhenBlank so Remove takes the key off.
    {
      name: "avatar",
      label: "Photo",
      kind: "image",
      maxLength: 2048,
      clearWhenBlank: true,
      image: { maxBytes: 5 * 1024 * 1024, shape: "portrait" },
      help: "A square photo of at least 400 by 400 pixels works best; it is cropped to a circle. PNG, JPEG or WebP, up to 5 MB. If the person later changes the photo on their own profile, theirs replaces this one.",
    },
  ],
  primary: (a) => a.name,
  secondary: (a) =>
    [a.email ?? "No email", a.status].filter(Boolean).join(" · "),
  searchFields: (a) => [a.name, a.email, a.company],
  toForm: (a) => ({
    name: a.name,
    email: a.email ?? "",
    role: a.role ?? "",
    company: a.company ?? "",
    attendeeType: a.attendeeType ?? "internal",
    status: a.status ?? "pending",
    bio: a.bio ?? "",
    linkedinUrl: a.linkedinUrl ?? "",
    avatar: a.avatar ?? "",
  }),
};
